import React from 'react';
import { Link } from 'react-router-dom';

export default function TermsPage() {
  return (
    <LegalPage title="Terms of Service" lastUpdated="March 2026">

  <Section title="1. Eligibility and Accounts">
    <p>
      You must be at least 18 years old to use Raji. You are responsible for your account, credentials, connected services, and all activity under your account. You agree to provide accurate information and to notify us promptly of any unauthorized access.
    </p>
  </Section>

  <Section title="2. Service Overview">
    <p>
      Raji is an AI-powered operations platform that can execute workflows, generate content, manage advertising campaigns, send communications, provision infrastructure, and perform automated tasks on your behalf.
      By using the service, you authorize Raji to act as your agent within the permissions and integrations you configure. You are responsible for reviewing outputs, monitoring activity, and managing costs.
      Raji is not a legal, financial, or professional advisor and should not be relied upon as your sole decision-making system.
    </p>
  </Section>

  <Section title="3. Connected Services and Authorization">
    <p>
      You may connect third-party services (such as email, code repositories, or advertising platforms). You authorize Raji to access and use these services to perform tasks on your behalf.
      Some features may use platform-managed accounts operated by Raji. You remain responsible for your content, actions, and compliance with third-party terms. We are not responsible for third-party outages, restrictions, or policy changes.
    </p>
  </Section>

  <Section title="4. Acceptable Use">
    <p>
      You agree not to use Raji to violate laws, infringe rights, send spam, create harmful or misleading content, bypass security controls, or misuse third-party platforms.
      You must comply with applicable laws (including anti-spam and advertising regulations) and the policies of any integrated services.
    </p>
  </Section>

  <Section title="5. Billing, Fees, and Payments">
    <p>
      Paid plans are billed via Stripe and may include subscriptions, usage-based charges, and add-ons. Subscriptions renew automatically unless canceled.
      By adding a payment method, you authorize us to charge for recurring fees and usage-based costs. Payments are non-refundable except where required by law.
    </p>
  </Section>

  <Section title="6. Advertising and Spend Authorization">
    <p>
      If you enable advertising features, you authorize Raji to create and manage campaigns and spend up to your configured daily budget.
      Charges may include advertising spend and a platform fee. Charges may be processed automatically without per-transaction approval.
      You are responsible for the content, targeting, and compliance of your campaigns. We are not responsible for ad performance, rejections, or platform actions.
    </p>
  </Section>

  <Section title="7. Payments from Your Customers">
    <p>
      If you accept payments through Raji, payments are processed by Stripe. You are the merchant of record and responsible for taxes, refunds, and compliance.
      Platform fees may apply, and funds may be subject to holding periods before withdrawal. Chargebacks and disputes remain your responsibility.
    </p>
  </Section>

  <Section title="8. AI and Generated Content">
    <p>
      Raji uses third-party AI providers to generate outputs. AI-generated content may be inaccurate or inappropriate, and you are responsible for reviewing and approving all outputs before use.
      You retain ownership of outputs generated for your use, subject to third-party provider terms.
    </p>
  </Section>

  <Section title="9. Infrastructure and Automation">
    <p>
      Raji may provision cloud resources and execute automated operations on your behalf. You are responsible for associated costs, configurations, and exported data.
      Certain features may run on scheduled or autonomous cycles without manual approval. You may disable these features at any time.
    </p>
  </Section>

  <Section title="10. Ownership and License">
    <p>
      You retain ownership of your content. You grant Raji a license to use, process, and store your content to operate and improve the service.
      Raji retains ownership of its platform, software, and underlying systems.
    </p>
  </Section>

  <Section title="11. Public Content and Visibility">
    <p>
      Some features may create publicly accessible dashboards or pages. You are responsible for reviewing and controlling what content is made public.
      We may remove content that violates these terms or applicable law.
    </p>
  </Section>

  <Section title="12. Privacy and Cookies">
    <p>
      Your use of the service is also governed by our Privacy Policy. We use cookies and similar technologies for authentication, functionality, and analytics.
    </p>
  </Section>

  <Section title="13. Disclaimers">
    <p>
      The service is provided “as is” without warranties of any kind. We do not guarantee accuracy, availability, performance, or outcomes, including advertising or AI-generated results.
    </p>
  </Section>

  <Section title="14. Limitation of Liability">
    <p>
      To the maximum extent permitted by law, Raji will not be liable for indirect, incidental, or consequential damages, including loss of profits or data.
      Our total liability will not exceed the greater of fees paid in the prior 12 months or $100 USD.
    </p>
  </Section>

  <Section title="15. Indemnification">
    <p>
      You agree to indemnify and hold Raji harmless from claims arising from your use of the service, your content, your campaigns, your communications, and your violations of law or third-party terms.
    </p>
  </Section>

  <Section title="16. Dispute Resolution">
    <p>
      You agree to attempt informal resolution before filing a claim. Disputes will be resolved by binding arbitration on an individual basis, unless otherwise required by law.
    </p>
  </Section>

  <Section title="17. Suspension and Termination">
    <p>
      We may suspend or terminate access if your use creates legal, security, or operational risk. Upon termination, services stop and data may be deleted after a reasonable period.
    </p>
  </Section>

  <Section title="18. Changes to Terms">
    <p>
      We may update these terms from time to time. Continued use of the service constitutes acceptance of the updated terms.
    </p>
  </Section>

  <Section title="19. Contact">
    <p>
      For questions, contact contact@raji.ai. For legal matters, contact legal@raji.ai.
    </p>
  </Section>

</LegalPage>
  );
}

function LegalPage({ title, lastUpdated, children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)', fontFamily: 'var(--sans)' }}>
      <main style={{ maxWidth: '720px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <Link to="/" style={{ fontFamily: 'var(--serif)', fontSize: '18px',
          color: 'var(--ink)', textDecoration: 'none', display: 'inline-block',
          marginBottom: '32px' }}>
          raji<span style={{ color: 'var(--accent)' }}>.</span>ai
        </Link>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: '36px',
          fontWeight: '400', marginBottom: '8px' }}>{title}</h1>
        <p style={{ fontSize: '13px', color: 'var(--ink-mute)',
          marginBottom: '40px' }}>Last updated: {lastUpdated}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {children}
        </div>
        <div style={{ marginTop: '48px', paddingTop: '24px',
          borderTop: '1px solid var(--paper-3)', fontSize: '12px',
          color: 'var(--ink-mute)' }}>
          <Link to="/terms" style={navLink}>Terms</Link>
          {' · '}
          <Link to="/privacy" style={navLink}>Privacy</Link>
          {' · '}
          <Link to="/" style={navLink}>Back to Raji.ai</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 style={{ fontSize: '16px', fontWeight: '600',
        marginBottom: '8px', color: 'var(--ink)' }}>{title}</h2>
      <div style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--ink-soft)' }}>
        {children}
      </div>
    </div>
  );
}

const navLink = {
  fontSize: '12px', color: 'var(--ink-mute)', textDecoration: 'none',
};